import React, { useState, useEffect, useMemo, useCallback } from "react";
import "../assets/Wallet/RequestWalletFund.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCreditCard,
  faPaperPlane,
  faHistory,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserProfile } from "../redux/slices/branch/userProfileSlice";
import { toast } from "react-toastify";

const RequestWalletFund = () => {
  const { profile,loading:profileLoading } = useSelector((state) => state.userProfile);
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    amount: "",
    purpose: "",
  });

  const [search, setSearch] = useState("");
  const [entries, setEntries] = useState(10);

  const [fundHistory, setFundHistory] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  /* FETCH PROFILE */
  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /* FETCH HISTORY (SAFE & REUSABLE) */
  const fetchFundHistory = useCallback(async () => {
    if (profileLoading) return;
    if (!profile?.superAdminId) return;

    try {
      setLoading(true);
      const res = await axios.get(
        "https://narrowitsolution.in/api/employe-fund-request/get-all",
        {
          params: {
            superAdminId: profile.superAdminId,
            page: currentPage,
            limit: entries,
          },
        }
      );

      if (res.data?.success) {
        setFundHistory(res.data.data || []);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  }, [profileLoading, profile?.superAdminId, currentPage, entries]);

  useEffect(() => {
    fetchFundHistory();
  }, [fetchFundHistory]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.amount || !formData.purpose) {
      toast.warn("Enter amount and purpose");
      return;
    }

    try {
      const res = await axios.post(
        "https://narrowitsolution.in/api/employe-fund-request/create",
        {
          employeeId: profile?.id,
          branchId: profile?.branchId,
          superAdminId: profile?.superAdminId,
          amount: formData.amount,
          purpose: formData.purpose,
          requestedBy: profile?.id,
        }
      );

      toast.success(res?.data?.message || "Fund requested");
      setFormData({ amount: "", purpose: "" });
      setCurrentPage(1); // refresh list from page 1
      fetchFundHistory();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Request failed");
    }
  };



  /* PAGINATION */
  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage((p) => p + 1);
  };

  /* 🔍 SEARCH */
  const filteredHistory = useMemo(() => {
    return fundHistory.filter((item) =>
      `${item.amount} ${item.status} ${item.purpose}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [fundHistory, search]);

  /* 📄 CSV DOWNLOAD */
  const downloadCSV = () => {
    const rows = [
      ["Date", "Amount", "Purpose", "Status"],
      ...filteredHistory.map((i) => [
        new Date(i.createdAt).toLocaleDateString(),
        i.amount,
        i.purpose,
        i.status,
      ]),
    ];

    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "wallet_requests.csv";
    link.click();
  };

  /* 📊 EXCEL */
  const downloadExcel = () => {
    const table = `
      <table>
        <tr><th>Date</th><th>Amount</th><th>Purpose</th><th>Status</th></tr>
        ${filteredHistory
          .map(
            (i) =>
              `<tr>
                <td>${new Date(i.createdAt).toLocaleDateString()}</td>
                <td>${i.amount}</td>
                <td>${i.purpose}</td>
                <td>${i.status}</td>
              </tr>`
          )
          .join("")}
      </table>
    `;

    const blob = new Blob([table], {
      type: "application/vnd.ms-excel",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "wallet_requests.xls";
    link.click();
  };

  /* 🖨 PRINT */
  const printTable = () => {
    const content = document.querySelector(
      ".WalletRequestFund-tableWrapper"
    ).innerHTML;

    const w = window.open("");
    w.document.write(`<html><body>${content}</body></html>`);
    w.print();
    w.close();
  };

  return (
    <div className="WalletRequestFund-container">
      {/* FORM */}
      <section className="WalletRequestFund-box">
        <h2 className="WalletRequestFund-title">
          <FontAwesomeIcon icon={faCreditCard} /> Request Wallet Fund
        </h2>

        <form
          className="WalletRequestFund-form-wrapper"
          onSubmit={handleSubmit}
        >
          <div className="WalletRequestFund-form">
            <div className="WalletRequestFund-input-field">
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="Amount"
                required
              />
            </div>

            <div className="WalletRequestFund-input-field">
              <input
                type="text"
                name="purpose"
                value={formData.purpose}
                onChange={handleChange}
                placeholder="Purpose"
                required
              />
            </div>
          </div>

          <div className="WalletRequestFund-submit">
            <button type="submit">
              <FontAwesomeIcon icon={faPaperPlane} /> Submit Request
            </button>
          </div>
        </form>
      </section>

      {/* HISTORY */}
      <section className="WalletRequestFund-box">
        <h2 className="WalletRequestFund-title">
          <FontAwesomeIcon icon={faHistory} /> Request History
        </h2>

        <div className="WalletRequestFund-actions">
          <div className="WalletRequestFund-buttons">
            <button className="WalletRequestFund-btn csv" onClick={downloadCSV}>
              <i className="fas fa-file-csv"></i> CSV
            </button>
            <button
              className="WalletRequestFund-btn excel"
              onClick={downloadExcel}
            >
             <i className="fas fa-file-excel"></i> Excel
            </button>
            <button
              className="WalletRequestFund-btn print"
              onClick={printTable}
            >
             <i className="fas fa-print"></i> Print
            </button>
          </div>

          <div className="WalletRequestFund-controls">
            <div className="WalletRequestFund-filter">
              <label>Show</label>
              <select
                value={entries}
                onChange={(e) => {
                  setEntries(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option>10</option>
                <option>25</option>
                <option>50</option>
                <option>100</option>
              </select>
              <span>entries</span>
            </div>

            <div className="WalletRequestFund-search">
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="WalletRequestFund-tableWrapper">
          <table className="WalletRequestFund-table">
            <thead>
              <tr>
                <th>S.N</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Purpose</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="empty">
                    Loading...
                  </td>
                </tr>
              ) : filteredHistory.length ? (
                filteredHistory.map((item, index) => (
                  <tr key={item.id}>
                    <td>
                      {(currentPage - 1) * entries + index + 1}
                    </td>
                    <td>
                      {new Date(item.createdAt).toLocaleDateString("en-GB")}
                    </td>
                    <td>₹ {item.amount}</td>
                    <td>{item.purpose}</td>
                    <td>
                      <span className={`status ${item.status?.toLowerCase()}`}>
                        {item.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="empty">
                    No request history
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="WalletRequestFund-pagination">
          <button onClick={handlePrev} disabled={currentPage === 1}>
            Prev
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={handleNext}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </section>
    </div>
  );
};

export default RequestWalletFund;
